import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { API, formatApiError } from "@/lib/api";
import ClienteShell from "@/components/ClienteShell";
import { ArrowLeft, Star, CheckCircle2, Instagram, Wrench, Send } from "lucide-react";

export default function ClientePosVenda() {
  const { servicoId } = useParams();
  const [estrelas, setEstrelas] = useState(0);
  const [hover, setHover] = useState(0);
  const [comentario, setComentario] = useState("");
  const [autorizou, setAutorizou] = useState(null);
  const [querAssistencia, setQuerAssistencia] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (!estrelas) { toast.error("Selecione uma nota"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/cliente/posvenda/${servicoId}`, {
        estrelas, comentario,
        autorizou_uso_sucesso: estrelas >= 4 ? autorizou : null,
        quer_assistencia: estrelas <= 3 ? querAssistencia : null,
      });
      setDone(true);
      toast.success("Obrigado pela avaliação!");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  if (done) {
    return (
      <ClienteShell step={4}>
        <div className="flex items-center justify-center py-10">
          <div className="text-center max-w-md aj-fade-up">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#FF6700] to-[#F97316] shadow-[0_10px_30px_-10px_rgba(234,88,12,.6)] mx-auto mb-6 flex items-center justify-center text-white">
              <CheckCircle2 size={32} />
            </div>
            <h1 className="font-display font-extrabold text-3xl text-[#0F172A]">Avaliação registrada</h1>
            <p className="text-[#64748B] mt-2">
              {estrelas <= 3 && querAssistencia
                ? "Recebemos sua solicitação de assistência. Em até 72h úteis avaliaremos. Prazo de entrega: até 30 dias."
                : "Obrigado por avaliar nosso atendimento!"}
            </p>
            {estrelas >= 4 && (
              <div className="mt-8 aj-card-elevated p-6 border-l-4 border-l-[#142766]">
                <div className="aj-eyebrow mb-2">Agradecemos sua avaliação</div>
                <p className="text-sm text-[#334155]">Siga nosso Instagram para acompanhar novos projetos:</p>
                <a href="https://instagram.com/ebano.marfim" target="_blank" rel="noreferrer"
                  className="btn-aj-primary mt-4 inline-flex" data-testid="ig-cta">
                  <Instagram size={17} /> @ebano.marfim
                </a>
              </div>
            )}
          </div>
        </div>
      </ClienteShell>
    );
  }

  return (
    <ClienteShell
      step={3}
      headerRight={
        <Link to="/" className="text-sm font-semibold text-[#142766] hover:text-[#FF6700] transition-colors inline-flex items-center gap-1.5" data-testid="posvenda-voltar-inicio">
          <ArrowLeft size={15} /> Início
        </Link>
      }
    >
      <div className="aj-fade-up">
        <div className="aj-eyebrow">Pesquisa de pós-venda</div>
        <h1 className="font-display font-extrabold text-3xl text-[#0F172A] mt-2">
          Como foi seu <span className="aj-accent-orange">atendimento?</span>
        </h1>
      </div>

      <div className="mt-8 aj-card p-6 sm:p-7 aj-fade-up-1">
        <div className="text-xs font-bold uppercase tracking-wider text-[#142766] mb-4">Sua nota</div>
        <div className="flex gap-2 sm:gap-3" data-testid="posvenda-stars">
          {[1, 2, 3, 4, 5].map((n) => {
            const active = (hover || estrelas) >= n;
            return (
              <button key={n}
                onMouseEnter={() => setHover(n)}
                onMouseLeave={() => setHover(0)}
                onClick={() => setEstrelas(n)}
                data-testid={`posvenda-star-${n}`}
                className={`rounded-xl border flex items-center justify-center transition-all ${
                  active
                    ? "bg-gradient-to-br from-[#F59E0B] to-[#FF6700] border-transparent text-white shadow-[0_6px_16px_-6px_rgba(234,88,12,.6)] scale-105"
                    : "bg-white border-[#E2E8F0] text-[#CBD5E1] hover:border-[#F59E0B]"
                }`}
                style={{ width: 56, height: 56 }}>
                <Star size={26} fill={active ? "currentColor" : "none"} />
              </button>
            );
          })}
        </div>
      </div>

      {estrelas > 0 && (
        <div className="mt-5 space-y-5 aj-fade-up">
          <div className="aj-card p-6">
            <div className="text-xs font-bold uppercase tracking-wider text-[#142766] mb-2">Comentário</div>
            <Textarea value={comentario} onChange={(e) => setComentario(e.target.value)} rows={4}
              placeholder={estrelas <= 3 ? "Por favor, descreva o que aconteceu" : "Deixe seu depoimento"}
              className="rounded-lg border-[#CBD5E1] focus-visible:ring-[#FF6700]"
              data-testid="posvenda-comentario" />
          </div>

          {estrelas >= 4 && (
            <div className="aj-card p-6 border-l-4 border-l-[#F59E0B]">
              <div className="text-xs font-bold uppercase tracking-wider text-[#E05C00] mb-3">
                Posso usar seu depoimento em divulgações?
              </div>
              <div className="flex gap-2">
                <Toggle active={autorizou === true} onClick={() => setAutorizou(true)} testid="posvenda-autorizo-sim">Sim, autorizo</Toggle>
                <Toggle active={autorizou === false} onClick={() => setAutorizou(false)} testid="posvenda-autorizo-nao">Não</Toggle>
              </div>
            </div>
          )}

          {estrelas <= 3 && (
            <div className="aj-card p-6 border-l-4 border-l-[#EF4444]">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#DC2626] mb-2">
                <Wrench size={13} /> Deseja abrir assistência técnica?
              </div>
              <p className="text-xs text-[#64748B] mb-3">
                Será avaliado antes da validação. Prazo de resposta: 72h úteis. Caso aprovado,
                prazo de entrega: até 30 dias com acompanhamento.
              </p>
              <div className="flex gap-2">
                <Toggle active={querAssistencia === true} onClick={() => setQuerAssistencia(true)} danger testid="posvenda-assist-sim">Sim, abrir chamado</Toggle>
                <Toggle active={querAssistencia === false} onClick={() => setQuerAssistencia(false)} testid="posvenda-assist-nao">Não</Toggle>
              </div>
            </div>
          )}

          <button onClick={submit} disabled={loading}
            className="btn-aj-primary w-full h-12 justify-center disabled:opacity-60"
            data-testid="posvenda-enviar">
            <Send size={16} /> {loading ? "Enviando…" : "Enviar avaliação"}
          </button>
        </div>
      )}
    </ClienteShell>
  );
}

function Toggle({ active, onClick, children, danger, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${
        active
          ? danger
            ? "bg-[#EF4444] border-[#EF4444] text-white"
            : "bg-[#142766] border-[#142766] text-white"
          : "bg-white border-[#CBD5E1] text-[#334155] hover:border-[#142766]"
      }`}>
      {children}
    </button>
  );
}
